import { useState } from 'react';

import { Button } from '../../button';

import {
  GUESS_LISTS,
  GUESS_RATE_LABELS,
  PRECOMPUTED_TABLE,
  guessingResult,
  hashLabels,
  initialLogin,
  initialSalt,
  loginReveals,
  loginTransition,
  responseText,
  saltRows,
  saltSummary,
  saltTransition,
  shortHex,
  storageCopy,
  storagePlaces,
  storageSummary,
  type GuessHash,
  type GuessList,
  type LoginAction,
  type LoginPassword,
  type LoginUsername,
  type SaltAction,
  type StorageMode,
  type StoragePlace,
} from './auth-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './auth-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  mono = [],
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  /** Column indexes shown in the monospace font (hashes, salts, bodies). */
  mono?: number[];
}) {
  const template = `repeat(${columns.length}, minmax(0, 1fr))`;
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span
                  role="cell"
                  key={column}
                  className={mono.includes(column) ? 'auth-mono' : undefined}
                >
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No answers yet</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const SIMULATION = 'A simulation with real hash values computed in advance.';

export function AuthStorageLab() {
  const [mode, setMode] = useState<StorageMode>('plain');
  const [place, setPlace] = useState<StoragePlace>('table');
  const copy = storageCopy(mode, place);
  const summary = storageSummary(mode);
  return (
    <Lab
      title="Who can read a stored password?"
      eyebrow="Passwords model · storage"
      className="rel-lab auth-lab"
    >
      <Predict>
        The server stores each password so it can check logins. Open each place the users table is
        copied to: how many passwords can someone read there, and what changes with an scrypt hash?
      </Predict>
      <div className="cb-controls">
        <label>
          Storage
          <ModelSelect
            aria-label="Storage"
            value={mode}
            onValueChange={(value) => setMode(value as StorageMode)}
            options={[
              { value: 'plain', label: 'Plain text' },
              { value: 'scrypt', label: 'scrypt hash' },
            ]}
          />
        </label>
      </div>
      <div className="cb-tabs" role="group" aria-label="Place">
        {storagePlaces.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-pressed={place === entry.id}
            onClick={() => setPlace(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>
      <Panel label={copy.label}>
        <p className="rel-caption">{copy.where}</p>
        {place === 'table' ? (
          <Table
            label="Users table"
            columns={['id', 'username', copy.column]}
            rows={copy.rows}
            mono={[2]}
          />
        ) : (
          <pre aria-label={copy.label}>{copy.text}</pre>
        )}
        <Facts
          values={[
            [
              'Passwords readable in this copy',
              copy.readable.length ? copy.readable.join(', ') : 'none',
            ],
          ]}
        />
      </Panel>
      <div className="cb-controls">
        <Reset
          onClick={() => {
            setMode('plain');
            setPlace('table');
          }}
        />
      </div>
      <Result tone={summary.passwords ? 'warning' : 'success'}>{summary.message}</Result>
      <p className="rel-caption">
        {SIMULATION} Long scrypt strings are shortened with …; each one is scrypt$16384$8$1$, a salt
        and a hash.
      </p>
    </Lab>
  );
}

export function AuthGuessingLab() {
  const [hash, setHash] = useState<GuessHash>('sha256');
  const [list, setList] = useState<GuessList>('10k');
  const result = guessingResult(hash, list);
  return (
    <Lab
      title="How long does a leaked hash survive a guess list?"
      eyebrow="Passwords model · guessing"
      className="rel-lab auth-lab"
    >
      <Predict>
        An attacker has a copy of the users table and a list of common passwords. Predict how long
        SHA-256 lasts against the 10 thousand list, then how much scrypt changes it.
      </Predict>
      <div className="cb-controls">
        <label>
          Hash
          <ModelSelect
            aria-label="Hash"
            value={hash}
            onValueChange={(value) => setHash(value as GuessHash)}
            options={[
              { value: 'sha256', label: hashLabels.sha256 },
              { value: 'scrypt', label: hashLabels.scrypt },
            ]}
          />
        </label>
        <label>
          List
          <ModelSelect
            aria-label="List"
            value={list}
            onValueChange={(value) => setList(value as GuessList)}
            options={(Object.keys(GUESS_LISTS) as GuessList[]).map((key) => ({
              value: key,
              label: GUESS_LISTS[key].label,
            }))}
          />
        </label>
        <Reset
          onClick={() => {
            setHash('sha256');
            setList('10k');
          }}
        />
      </div>
      <Facts
        values={[
          ['Guesses per second', GUESS_RATE_LABELS[hash]],
          ['Common passwords on the list', GUESS_LISTS[list].label],
          ['Time to try the whole list against one account', result.time],
          ["Ravi's summer2026", result.raviFact],
        ]}
      />
      <Result tone={hash === 'sha256' ? 'warning' : 'neutral'}>{result.message}</Result>
      <p className="rel-caption">
        {SIMULATION} Each guess is one hash of a candidate password, compared with the stored value.
        The time is the list size divided by the guesses per second.
      </p>
    </Lab>
  );
}

export function AuthSaltLab() {
  const [state, setState] = useState(initialSalt);
  const act = (action: SaltAction) => setState((current) => saltTransition(current, action));
  const rows = saltRows(state);
  const summary = saltSummary(state);
  return (
    <Lab
      title="What do two equal passwords look like in the table?"
      eyebrow="Passwords model · salt"
      className="rel-lab auth-lab"
    >
      <Predict>
        Ravi and Jo both chose summer2026. Predict what their two rows look like, and how many
        accounts one lookup in a precomputed table cracks, with and without a salt.
      </Predict>
      <div className="cb-controls">
        <label>
          Salt
          <ModelSelect
            aria-label="Salt"
            value={state.salted ? 'on' : 'off'}
            onValueChange={(value) => act({ kind: 'set-salt', salted: value === 'on' })}
            options={[
              { value: 'off', label: 'Off' },
              { value: 'on', label: 'On' },
            ]}
          />
        </label>
        <Button size="sm" onClick={() => act({ kind: 'lookup' })}>
          Look up a precomputed table
        </Button>
        <Reset onClick={() => act({ kind: 'reset' })} />
      </div>
      <Table
        label="Stored passwords"
        columns={['user', 'salt', 'stored SHA-256', 'lookup finds']}
        mono={[1, 2]}
        rows={rows.map((row) => [
          row.name,
          row.salt === null ? '(none)' : shortHex(row.salt, 8),
          shortHex(row.stored),
          state.looked ? (row.found ?? 'nothing') : '(not looked up)',
        ])}
      />
      <Panel label="Precomputed table (part)">
        <Table
          label="Precomputed table"
          columns={['SHA-256', 'password']}
          mono={[0]}
          rows={PRECOMPUTED_TABLE.map((entry) => [shortHex(entry.hash), entry.password])}
        />
      </Panel>
      <Result
        tone={
          state.looked
            ? summary.cracked
              ? 'warning'
              : 'success'
            : summary.equal
              ? 'warning'
              : 'neutral'
        }
      >
        {summary.message}
      </Result>
      <p className="rel-caption">
        {SIMULATION} With a salt the stored value is SHA-256 of the salt followed by the password.
        The salts here are fixed examples; real code draws a new random one for each user.
      </p>
    </Lab>
  );
}

export function AuthLoginLab() {
  const [state, setState] = useState(initialLogin);
  const act = (action: LoginAction) => setState((current) => loginTransition(current, action));
  const last = state.last;
  const reveal = last ? loginReveals(last, state) : null;
  return (
    <Lab
      title="What does a failed login reveal?"
      eyebrow="Passwords model · login"
      className="rel-lab auth-lab"
    >
      <Predict>
        Try maya with a wrong password, then mya, which is not an account. Predict which answer
        tells an attacker that mya does not exist, and how.
      </Predict>
      <div className="cb-controls">
        <label>
          Username
          <ModelSelect
            aria-label="Username"
            value={state.username}
            onValueChange={(value) =>
              act({ kind: 'set-username', username: value as LoginUsername })
            }
            options={[
              { value: 'maya', label: 'maya' },
              { value: 'mya', label: 'mya' },
            ]}
          />
        </label>
        <label>
          Password
          <ModelSelect
            aria-label="Password"
            value={state.password}
            onValueChange={(value) =>
              act({ kind: 'set-password', password: value as LoginPassword })
            }
            options={[
              { value: 'right', label: 'Right password' },
              { value: 'wrong', label: 'Wrong password' },
            ]}
          />
        </label>
        <Button size="sm" onClick={() => act({ kind: 'submit' })}>
          Log in
        </Button>
        <Reset onClick={() => act({ kind: 'reset' })} />
      </div>
      <div className="cb-checks" role="group" aria-label="Server">
        <label>
          <input
            type="checkbox"
            checked={state.sameMessage}
            onChange={(event) => act({ kind: 'set-same-message', on: event.target.checked })}
          />
          Same message for every failure
        </label>
        <label>
          <input
            type="checkbox"
            checked={state.dummyHash}
            onChange={(event) => act({ kind: 'set-dummy-hash', on: event.target.checked })}
          />
          Check a dummy hash for unknown users
        </label>
      </div>
      <div className="cb-two">
        <Panel label="Request">
          <pre aria-label="Request">{last ? last.request : '(press Log in)'}</pre>
        </Panel>
        <Panel label="Response">
          <pre aria-label="Response">{last ? responseText(last) : '(no answer yet)'}</pre>
          <Facts values={[['Time to answer', last ? last.time : '—']]} />
        </Panel>
      </div>
      <Table
        label="Answers so far"
        columns={['username', 'password', 'answer', 'time']}
        rows={state.log.map((answer) => [
          answer.username,
          answer.password,
          `${answer.status} ${answer.detail}`,
          answer.time,
        ])}
      />
      <Result tone={reveal ? (reveal.reveals ? 'warning' : 'success') : 'neutral'}>
        {reveal ? reveal.message : 'Choose a username and a password, then press Log in.'}
      </Result>
      <p className="rel-caption">
        {SIMULATION} Checking a password with scrypt takes about 100 ms. Returning before any hash
        takes about 1 ms, and an attacker can measure that difference.
      </p>
    </Lab>
  );
}
